import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { emailMonitorAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface ScoreData {
  id: number;
  domain: string;
  overall_score: number;
  spf_score: number;
  dkim_score: number;
  dmarc_score: number;
  mx_score: number;
  blacklist_score: number;
  open_issues_count: number;
  scanned_at: string;
}

interface ChangeItem {
  id: number;
  domain: string;
  record_type: string;
  selector: string | null;
  change_type: string;
  old_value: string | null;
  new_value: string | null;
  detected_at: string;
}

const EmailMonitorDashboardPage: React.FC = () => {
  useDocumentTitle('Email Security');
  const { currentAsset } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scores, setScores] = useState<ScoreData[]>([]);
  const [recentChanges, setRecentChanges] = useState<ChangeItem[]>([]);
  const [activeAlerts, setActiveAlerts] = useState(0);
  const [blacklistSummary, setBlacklistSummary] = useState({ total_checks: 0, listed_count: 0 });
  const [hasBeenScanned, setHasBeenScanned] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!currentAsset) return;
    try {
      setLoading(true);
      const response = await emailMonitorAPI.getDashboard(currentAsset.id);
      if (response.data.status === 'success') {
        const data = response.data.data;
        setScores(data.scores || []);
        setRecentChanges(data.recent_changes || []);
        setActiveAlerts(data.active_alerts || 0);
        setBlacklistSummary(data.blacklist_summary || { total_checks: 0, listed_count: 0 });
        setHasBeenScanned(data.has_been_scanned);
      }
    } catch (error: any) {
      if (error?.response?.status === 403) {
        toast.error('Email monitoring is not available on your current plan');
        navigate('/billing/plans');
      } else {
        console.error('Failed to fetch email monitor dashboard:', error);
        toast.error('Failed to load email security dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [currentAsset, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleScan = async () => {
    if (!currentAsset) return;
    try {
      setScanning(true);
      const response = await emailMonitorAPI.triggerScan(currentAsset.id);
      if (response.data.status === 'success') {
        toast.success('Email security scan completed');
        await fetchDashboard();
      }
    } catch (error: any) {
      if (error?.response?.status === 429) {
        toast.error('Please wait 10 minutes between scans');
      } else {
        toast.error('Failed to run scan');
      }
    } finally {
      setScanning(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'danger';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return '#059669';
    if (score >= 50) return '#d97706';
    return '#dc2626';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!currentAsset) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="bi bi-envelope-check" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
            <p className="mt-3 text-muted">Please select an asset to view email security monitoring</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <p className="mt-3 text-muted">Loading email security data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid p-0">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1 fw-bold">Email Security Dashboard</h4>
            <p className="text-muted mb-0">Monitor your domain's email authentication and security posture</p>
          </div>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleScan}
            disabled={scanning}
          >
            {scanning ? (
              <><span className="spinner-border spinner-border-sm"></span> Scanning...</>
            ) : (
              <><i className="bi bi-arrow-clockwise"></i> Scan Now</>
            )}
          </button>
        </div>

        {!hasBeenScanned ? (
          /* First-time scan prompt */
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-shield-check" style={{ fontSize: '4rem', color: '#2563eb' }}></i>
              <h5 className="mt-3 fw-bold">No Scan Data Yet</h5>
              <p className="text-muted mb-4">Run your first email security scan to check SPF, DKIM, DMARC records and blacklist status.</p>
              <button className="btn btn-primary btn-lg" onClick={handleScan} disabled={scanning}>
                {scanning ? 'Scanning...' : 'Run First Scan'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Score Cards per Domain */}
            {scores.map((score) => (
              <div key={score.domain} className="mb-4">
                <h6 className="text-muted mb-3">
                  <i className="bi bi-globe me-2"></i>{score.domain}
                  <small className="ms-2 text-secondary">Last scanned: {formatDate(score.scanned_at)}</small>
                </h6>

                <div className="row g-3 mb-3">
                  {/* Overall Score */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                          style={{ width: '64px', height: '64px', background: getScoreBg(score.overall_score), color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}
                        >
                          {score.overall_score}
                        </div>
                        <div className="fw-semibold">Overall Score</div>
                      </div>
                    </div>
                  </div>

                  {/* SPF */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100 cursor-pointer" onClick={() => navigate('/email-monitor/dns-records')}>
                      <div className="card-body text-center">
                        <div className={`fs-2 fw-bold text-${getScoreColor(score.spf_score * 4)}`}>
                          {score.spf_score}/25
                        </div>
                        <div className="fw-semibold">SPF</div>
                        <small className="text-muted">Sender Policy</small>
                      </div>
                    </div>
                  </div>

                  {/* DKIM */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100 cursor-pointer" onClick={() => navigate('/email-monitor/dns-records')}>
                      <div className="card-body text-center">
                        <div className={`fs-2 fw-bold text-${getScoreColor(score.dkim_score * 4)}`}>
                          {score.dkim_score}/25
                        </div>
                        <div className="fw-semibold">DKIM</div>
                        <small className="text-muted">DomainKeys</small>
                      </div>
                    </div>
                  </div>

                  {/* DMARC */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100 cursor-pointer" onClick={() => navigate('/email-monitor/dns-records')}>
                      <div className="card-body text-center">
                        <div className={`fs-2 fw-bold text-${getScoreColor(score.dmarc_score * 4)}`}>
                          {score.dmarc_score}/25
                        </div>
                        <div className="fw-semibold">DMARC</div>
                        <small className="text-muted">Auth Reporting</small>
                      </div>
                    </div>
                  </div>

                  {/* MX */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100 cursor-pointer" onClick={() => navigate('/email-monitor/dns-records')}>
                      <div className="card-body text-center">
                        <div className={`fs-2 fw-bold text-${getScoreColor(score.mx_score * 4)}`}>
                          {score.mx_score}/25
                        </div>
                        <div className="fw-semibold">MX</div>
                        <small className="text-muted">Mail Exchange</small>
                      </div>
                    </div>
                  </div>

                  {/* Blacklist */}
                  <div className="col-md-4 col-lg-2">
                    <div className="card border-0 shadow-sm h-100 cursor-pointer" onClick={() => navigate('/email-monitor/blacklist')}>
                      <div className="card-body text-center">
                        <div className={`fs-2 fw-bold text-${getScoreColor(score.blacklist_score)}`}>
                          {score.blacklist_score}%
                        </div>
                        <div className="fw-semibold">Blacklist</div>
                        <small className="text-muted">Clean Status</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Issues Summary */}
                {score.open_issues_count > 0 && (
                  <div className="alert alert-danger d-flex align-items-center py-2" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <span>
                      <strong>{score.open_issues_count} open issue{score.open_issues_count > 1 ? 's' : ''}</strong> found for {score.domain}.
                      <a href="/email-monitor/dns-records" className="alert-link ms-1" onClick={(e) => { e.preventDefault(); navigate('/email-monitor/dns-records'); }}>View details</a>
                    </span>
                  </div>
                )}
              </div>
            ))}

            {/* Summary Row */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', background: activeAlerts > 0 ? '#fef2f2' : '#f0fdf4' }}>
                      <i className={`bi bi-bell-fill ${activeAlerts > 0 ? 'text-danger' : 'text-success'}`}></i>
                    </div>
                    <div>
                      <div className="fw-bold fs-5">{activeAlerts}</div>
                      <small className="text-muted">Active Alerts</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', background: blacklistSummary.listed_count > 0 ? '#fef2f2' : '#f0fdf4' }}>
                      <i className={`bi bi-shield-fill-x ${blacklistSummary.listed_count > 0 ? 'text-danger' : 'text-success'}`}></i>
                    </div>
                    <div>
                      <div className="fw-bold fs-5">
                        {blacklistSummary.listed_count > 0 ? `${blacklistSummary.listed_count} Listed` : 'Clean'}
                      </div>
                      <small className="text-muted">Blacklist Status ({blacklistSummary.total_checks} providers)</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', background: '#eff6ff' }}>
                      <i className="bi bi-clock-history text-primary"></i>
                    </div>
                    <div>
                      <div className="fw-bold fs-5">{recentChanges.length}</div>
                      <small className="text-muted">Recent DNS Changes</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Changes */}
            {recentChanges.length > 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold">Recent DNS Changes</h6>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => navigate('/email-monitor/dns-history')}>
                    View All
                  </button>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Domain</th>
                          <th>Record</th>
                          <th>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentChanges.map((change) => (
                          <tr key={change.id}>
                            <td><small>{formatDate(change.detected_at)}</small></td>
                            <td>{change.domain}</td>
                            <td>
                              <span className="badge bg-secondary">{change.record_type.toUpperCase()}</span>
                              {change.selector && <small className="ms-1 text-muted">({change.selector})</small>}
                            </td>
                            <td>
                              <span className={`badge bg-${change.change_type === 'added' ? 'success' : change.change_type === 'removed' ? 'danger' : 'warning'}`}>
                                {change.change_type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmailMonitorDashboardPage;
